import type { HygieneConfidence } from '../types/hygiene';
import type { CheckVisual } from './hygiene-presentation';
import {
  WALLET_TOKENS_UNREADABLE,
  WALLET_TRACKING_OFF,
  walletTokensUntracked
} from './holding-format';
import { HEALTH_REFERENCE } from '@/lib/health-reference';
import { displaySymbol } from '@/lib/token-symbol';
import { formatBasisPoints } from '@/lib/format/percent/basis-points';
import { formatFlooredPercent } from '@/lib/format/percent/floored';
import { formatPercent } from '@/lib/format/percent/percent';
import { formatCompactUsd } from '@/lib/format/usd/compact-usd';
import {
  acceptedWithdrawableNow,
  isWithdrawableNowBasis,
  listedValueUsd,
  type WithdrawableNowTotalInput
} from '@/lib/investor-analytics';

/**
 * Portfolio health — client-side v1 derivation.
 *
 * The fallback for deployments where the canonical Hygiene endpoint
 * (docs/features/portfolio-hygiene.md) is switched off. Overview derives an
 * advisory health assessment from the live investor endpoints instead:
 * /holdings (idle capital, asset quality), /risk (diversification) and
 * /liquidity (one-day exit share).
 *
 * It scores practices only, as the canonical path does (owner decision
 * 2026-10-02). Realised drawdown and volatility are outcomes: they are shown
 * as results on Risk & liquidity and never feed this score. Blending them
 * in let a bad month lower a score that is meant to say how the portfolio
 * is run.
 *
 * Contract kept from the hygiene spec:
 * - missing evidence is never scored — a driver without sufficient inputs is
 *   `gathering`, lowers coverage, and is excluded from the weighted mean;
 * - a numeric overall score publishes only at >= 60% evidence coverage, and
 *   is flagged `provisional` below 80%;
 * - thresholds come from the published ROWA reference profile
 *   (`@/lib/health-reference`), not from an inferred mandate. The assessment is advisory and never
 *   authorizes or executes anything.
 */

type HealthDriverId =
  | 'diversification'
  | 'idle-capital'
  | 'liquidity'
  | 'asset-quality';

type HealthDriverState =
  | 'sound'
  | 'watch'
  | 'at-risk'
  | 'gathering'
  | 'awaiting-allocation'
  // Unscored for good on this network: the evidence will not arrive.
  | 'not-tracked';

export type HealthBand =
  | 'strong'
  | 'sound'
  | 'needs-attention'
  | 'at-risk'
  | 'insufficient-evidence';

export interface HealthDriver {
  /** Derived-driver id or canonical hygiene category id. */
  id: string;
  label: string;
  weight: number;
  /** 0–100, or null when the required evidence is missing. */
  score: number | null;
  state: HealthDriverState;
  stateLabel: string;
  /** Plain-language evidence fact behind the score. */
  detail: string;
  /** The one check that best explains this area right now (canonical). */
  insight?: string;
  /** Per-check visuals for the tile's third layer (canonical only). */
  checkStates?: CheckVisual[];
}

export interface HealthAssessment {
  score: number | null;
  band: HealthBand;
  bandLabel: string;
  provisional: boolean;
  /** Evaluated weight over total weight, in basis points. */
  coverageBps: number;
  drivers: HealthDriver[];
  /** Canonical backend hygiene when available; client-side interim otherwise. */
  source: 'canonical' | 'derived';
  /** e.g. "ROWA reference · Balanced · definition v1.0.0" (canonical only). */
  attribution?: string;
  /** Plain-language critical-cap explanations to foreground (canonical only). */
  caps?: string[];
  /** A canonical recompute is in flight; the shown payload is the last completed one. */
  refreshing?: boolean;
  /** Why the score is withheld — short, human, frontend-owned phrasing. */
  evidenceNotes?: string[];
  /** The raw canonical provenance messages, for the methodology dialog. */
  evidenceDetails?: string[];
  /** v2 data-confidence block (signals + evidence age), canonical only. */
  confidence?: HygieneConfidence;
}

export interface HealthImprovement {
  id: string;
  title: string;
  detail: string;
  driverDelta: { label: string; from: number; to: number } | null;
  /** Modeled overall score improvement; null for evidence repairs. */
  scoreDelta: number | null;
  /** Custom chip label when neither a score delta nor a driver delta fits. */
  impactLabel?: string;
  /** Result-line text when there is no numeric driver delta to show. */
  resultLabel?: string;
  tradeoff: string;
  actionLabel: string;
  href: string;
}

export interface HealthInputs {
  holdings?: {
    /**
     * The portfolio total in dollars on one stated basis, when it is every
     * holding at its dollar value (`valueHoldings(...).total.reconciled`).
     * Null when no such total can be stated: the served `coveredValueUsd`
     * adds a vault in another currency at its own amount, so a share of it
     * is not a share of anything, and the idle driver waits instead.
     */
    reconciledTotalUsd: number | null;
    totals: {
      rowaValueUsd: string;
      externalValueUsd: string;
      cashValueUsd: string;
      coveredValueUsd: string;
    };
    coverage: {
      holdingsTotal: number;
      pricedCount: number;
      pricedBps: number | null;
      classifiedCount: number;
      classifiedBps: number | null;
      externalIncluded?: boolean;
      // A vocabulary that can grow; only 'disabled' is read here.
      externalStatus?: string;
    };
  };
  risk?: {
    // Null since the /risk schema made concentration measurable-or-null:
    // an unmeasurable concentration never scores, it just doesn't fire.
    concentration: {
      topAsset: {
        address?: string;
        symbol: string | null;
        tokenSymbol?: string | null;
        weightBps: number;
      } | null;
      top1WeightBps: number | null;
      hhiBps: number | null;
      diversificationScore: number | null;
    };
    vaultConcentration: {
      top1WeightBps: number | null;
      hhiBps: number | null;
    };
    exposures: { byAsset: { weightBps: number }[] };
    /** Null when the served counts could not be verified. */
    exposureCoverage: { pricedBps: number | null; allocatedBps: number } | null;
  };
  liquidity?: {
    /** The served basis; absent reads as the route projection. */
    basis?: string;
    coverageBps: number | null;
    totalValueUsd: string;
    /**
     * API 1.155.0: the served liquidity itself, positions included, so the
     * score reads the same accepted figures as the Risk & liquidity
     * screen. Without it liquidity is not measured on that basis.
     */
    served?: WithdrawableNowTotalInput;
    horizon: { bucket: string; typical: { cumulativeBps: number } }[];
  };
}

const DRIVER_LABELS: Record<HealthDriverId, string> = {
  diversification: 'Diversification',
  'idle-capital': 'Idle capital',
  liquidity: 'Liquidity',
  'asset-quality': 'Asset quality'
};

/**
 * The four practices, totalling 100: set to 30/30/20/20 (owner decision,
 * 2026-10-02) when the two outcome drivers were dropped. Diversification
 * and liquidity keep the lead they had over idle capital and asset quality.
 */
const DRIVER_WEIGHTS: Record<HealthDriverId, number> = {
  diversification: 30,
  liquidity: 30,
  'idle-capital': 20,
  'asset-quality': 20
};

// Presence is loud, absence is calm: scored states keep their all-caps
// status voice; unscored states use quiet sentence case.
const STATE_LABELS: Record<HealthDriverState, string> = {
  sound: 'SOUND',
  watch: 'WATCH',
  'at-risk': 'AT RISK',
  gathering: 'Gathering data',
  'awaiting-allocation': 'Awaiting allocation',
  'not-tracked': 'Not tracked'
};

export const HEALTH_BAND_LABELS: Record<HealthBand, string> = {
  strong: 'STRONG',
  sound: 'SOUND',
  'needs-attention': 'NEEDS ATTENTION',
  'at-risk': 'AT RISK',
  'insufficient-evidence': 'INSUFFICIENT EVIDENCE'
};

/** Clamped piecewise-linear interpolation across [x, score] anchors. */
function piecewise(
  value: number,
  anchors: readonly [number, number][]
): number {
  const first = anchors[0]!;
  const last = anchors[anchors.length - 1]!;
  if (value <= first[0]) return first[1];
  if (value >= last[0]) return last[1];
  for (let index = 1; index < anchors.length; index += 1) {
    const [x1, y1] = anchors[index]!;
    const [x0, y0] = anchors[index - 1]!;
    if (value <= x1) {
      return y0 + ((value - x0) / (x1 - x0)) * (y1 - y0);
    }
  }
  return last[1];
}

function stateForScore(score: number | null): HealthDriverState {
  if (score == null) return 'gathering';
  if (score >= 75) return 'sound';
  if (score >= 60) return 'watch';
  return 'at-risk';
}

function driver(
  id: HealthDriverId,
  score: number | null,
  detail: string,
  unscoredState: Extract<
    HealthDriverState,
    'gathering' | 'awaiting-allocation' | 'not-tracked'
  > = 'gathering'
): HealthDriver {
  const rounded = score == null ? null : Math.round(score);
  const state = rounded == null ? unscoredState : stateForScore(rounded);
  return {
    id,
    label: DRIVER_LABELS[id],
    weight: DRIVER_WEIGHTS[id],
    score: rounded,
    state,
    stateLabel: STATE_LABELS[state],
    detail
  };
}

function parseUsd(value: string | undefined): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

// The idle share is missing evidence, never scored, while the wallet
// tokens its cash figure is made of were not read.
const UNTRACKED_IDLE_DETAIL = {
  disabled: WALLET_TRACKING_OFF,
  unavailable: WALLET_TOKENS_UNREADABLE
} as const;

function idleShareOf(inputs: HealthInputs): {
  covered: number;
  cash: number;
  share: number;
} | null {
  const holdings = inputs.holdings;
  if (!holdings) return null;
  if (walletTokensUntracked(holdings.coverage) != null) return null;
  // Never the served `coveredValueUsd`: it mixes currencies.
  const covered = holdings.reconciledTotalUsd;
  if (covered == null || !Number.isFinite(covered) || covered <= 0) {
    return null;
  }
  // Cash rows are wallet tokens, natively priced in dollars.
  const cash = parseUsd(holdings.totals.cashValueUsd);
  return { covered, cash, share: cash / covered };
}

function idleScore(share: number): number {
  return piecewise(share, [
    [HEALTH_REFERENCE.idleReserveShare, 100],
    [HEALTH_REFERENCE.idleWatchShare, 60],
    [HEALTH_REFERENCE.idleFloorShare, 0]
  ]);
}

/**
 * The share of the priced total the vaults would pay out now (API 1.155.0),
 * floored. Its horizon is flat, so the one day bucket reads 100% of the
 * covered value whatever the share is, and is never read here. On this
 * basis coverage IS the share: a low one is a reading, not missing
 * evidence, so the route projection's coverage gate does not apply.
 *
 * The same share the Risk & liquidity screen shows, from the one shared
 * rule (`acceptedWithdrawableNow`): accepted only when every served
 * aggregate agrees with its positions, reported once for both screens.
 */
function withdrawableNowBps(
  liquidity: NonNullable<HealthInputs['liquidity']>
): number | null {
  if (liquidity.served == null) return null;
  // The score covers the whole wallet: a vault with no price, or one the
  // list does not reach, is a part nothing measured, so there is no score.
  if (unmeasuredHolding(liquidity.served) != null) return null;
  return acceptedWithdrawableNow(liquidity.served)?.shareBps ?? null;
}

/** Why the share withdrawable now is not scored, in the reader's words. */
function unmeasuredLiquidityDetail(
  liquidity: NonNullable<HealthInputs['liquidity']>
): string {
  const outside = liquidity.served && unmeasuredHolding(liquidity.served);
  if (outside) return outside;
  const held = liquidity.served
    ? (listedValueUsd(liquidity.served.positions) ?? undefined)
    : liquidity.totalValueUsd;
  return parseUsd(held) > 0
    ? 'Not measured yet: a vault couldn’t be read'
    : 'Not measured yet: no position has a price yet';
}

/** Why part of what is held is outside the measured figures, if it is. */
function unmeasuredHolding(served: WithdrawableNowTotalInput): string | null {
  if (served.positions.some((position) => position.valueUsd == null)) {
    return 'Not measured yet: a vault has no price yet';
  }
  if (
    served.totalPositions != null &&
    served.totalPositions > served.positions.length
  ) {
    return 'Not measured yet: not every vault could be listed';
  }
  return null;
}

function oneDayBps(inputs: HealthInputs): number | null {
  const liquidity = inputs.liquidity;
  if (!liquidity) return null;
  if (isWithdrawableNowBasis(liquidity.basis ?? '')) {
    return withdrawableNowBps(liquidity);
  }
  // Written as an explicit null test on purpose: `null < 5000` evaluates to
  // false, so a shorthand comparison would treat an unmeasurable portfolio
  // as well covered and score it. API v1.57.0 made this reachable.
  if (liquidity.coverageBps == null || liquidity.coverageBps < 5000) {
    return null;
  }
  if (parseUsd(liquidity.totalValueUsd) <= 0) return null;
  const bucket = liquidity.horizon.find((entry) => entry.bucket === 't1');
  return bucket ? bucket.typical.cumulativeBps : null;
}

function liquidityScore(t1Bps: number): number {
  return piecewise(t1Bps, [
    [0, 20],
    [HEALTH_REFERENCE.oneDayLiquidityReferenceBps, 70],
    [HEALTH_REFERENCE.oneDayLiquiditySoundBps, 100]
  ]);
}

/** Positively established "holds no ROWA vault positions" — never inferred
 *  from a missing response. */
function hasNoVaultPositions(inputs: HealthInputs): boolean {
  const totals = inputs.holdings?.totals;
  return totals != null && parseUsd(totals.rowaValueUsd) <= 0;
}

function deriveDrivers(inputs: HealthInputs): HealthDriver[] {
  const drivers: HealthDriver[] = [];
  const awaitingAllocation = hasNoVaultPositions(inputs);

  // Diversification — backend HHI score over looked-through assets. A wallet
  // with no vault positions is not "gathering": there is nothing to look
  // through until the first allocation.
  const risk = inputs.risk;
  if (
    risk?.exposureCoverage?.pricedBps != null &&
    risk.exposureCoverage.pricedBps > 0 &&
    risk.exposures.byAsset.length > 0
  ) {
    drivers.push(
      driver(
        'diversification',
        risk.concentration.diversificationScore,
        `${risk.exposures.byAsset.length} asset${risk.exposures.byAsset.length === 1 ? '' : 's'} · largest ${formatBasisPoints(risk.concentration.top1WeightBps, { fractionDigits: 0 })}`
      )
    );
  } else if (risk && awaitingAllocation) {
    drivers.push(
      driver(
        'diversification',
        null,
        'No ROWA vault positions yet',
        'awaiting-allocation'
      )
    );
  } else {
    drivers.push(
      driver('diversification', null, 'Needs priced underlying exposure')
    );
  }

  // Idle capital — cash share of covered portfolio value, only when the
  // wallet tokens the cash figure is made of were actually read.
  const idle = idleShareOf(inputs);
  const untracked = walletTokensUntracked(inputs.holdings?.coverage);
  if (untracked != null) {
    drivers.push(
      driver(
        'idle-capital',
        null,
        UNTRACKED_IDLE_DETAIL[untracked],
        // Switched off on this network: that data never arrives, so the
        // tile must not say it is being gathered. Out for now: it will.
        untracked === 'disabled' ? 'not-tracked' : 'gathering'
      )
    );
  } else if (idle) {
    drivers.push(
      driver(
        'idle-capital',
        idleScore(idle.share),
        `${formatCompactUsd(idle.cash, { maximumFractionDigits: 2 })} · ${formatPercent(idle.share, { from: 'fraction', digits: 1 })} of portfolio`
      )
    );
  } else {
    drivers.push(
      driver('idle-capital', null, 'Needs a priced portfolio value')
    );
  }

  // Liquidity — share of covered value exitable within one day. Same rule:
  // no vault positions means no exit routes to project, not missing data.
  const t1 = oneDayBps(inputs);
  if (t1 != null) {
    drivers.push(
      driver(
        'liquidity',
        liquidityScore(t1),
        // Floored: a share that can be paid out never reads higher.
        isWithdrawableNowBasis(inputs.liquidity?.basis ?? '')
          ? `${formatFlooredPercent(t1)} withdrawable now`
          : `${formatBasisPoints(t1, { fractionDigits: 0 })} exitable within one day`
      )
    );
  } else if (inputs.liquidity && awaitingAllocation) {
    drivers.push(
      driver(
        'liquidity',
        null,
        'No ROWA vault positions yet',
        'awaiting-allocation'
      )
    );
  } else if (
    inputs.liquidity &&
    isWithdrawableNowBasis(inputs.liquidity.basis ?? '')
  ) {
    drivers.push(
      driver('liquidity', null, unmeasuredLiquidityDetail(inputs.liquidity))
    );
  } else {
    drivers.push(driver('liquidity', null, 'Needs exit route coverage'));
  }

  // Asset quality — priced and registry-classified holdings coverage.
  const coverage = inputs.holdings?.coverage;
  if (
    coverage &&
    coverage.holdingsTotal > 0 &&
    coverage.pricedBps != null &&
    coverage.classifiedBps != null
  ) {
    drivers.push(
      driver(
        'asset-quality',
        coverage.pricedBps / 200 + coverage.classifiedBps / 200,
        `${formatBasisPoints(coverage.pricedBps, { fractionDigits: 0 })} priced · ${formatBasisPoints(coverage.classifiedBps, { fractionDigits: 0 })} classified`
      )
    );
  } else if (coverage && coverage.holdingsTotal > 0) {
    // Holdings exist but ROWA cannot measure how much of them it can price.
    // Scoring that as zero would read as a portfolio of junk.
    drivers.push(driver('asset-quality', null, 'Waiting on data'));
  } else {
    drivers.push(driver('asset-quality', null, 'Needs recognized holdings'));
  }

  return drivers;
}

function overallFromDrivers(drivers: HealthDriver[]): {
  score: number | null;
  coverageBps: number;
} {
  const evaluated = drivers.filter(
    (entry): entry is HealthDriver & { score: number } => entry.score != null
  );
  const evaluatedWeight = evaluated.reduce(
    (sum, entry) => sum + entry.weight,
    0
  );
  const totalWeight = drivers.reduce((sum, entry) => sum + entry.weight, 0);
  const coverageBps =
    totalWeight > 0 ? Math.round((evaluatedWeight / totalWeight) * 10_000) : 0;

  if (coverageBps < HEALTH_REFERENCE.minCoverageBps) {
    return { score: null, coverageBps };
  }

  const weighted = evaluated.reduce(
    (sum, entry) => sum + entry.weight * entry.score,
    0
  );
  return {
    score: Math.round(weighted / evaluatedWeight),
    coverageBps
  };
}

/**
 * No score, and what stands in the way is the first vault allocation: some
 * check waits on a vault position, not on data. Waiting will not bring the
 * score; allocating will, so that is what the card says.
 */
export function scoreAwaitsFirstAllocation(
  assessment: Pick<HealthAssessment, 'score' | 'drivers'>
): boolean {
  return (
    assessment.score == null &&
    assessment.drivers.some((entry) => entry.state === 'awaiting-allocation')
  );
}

/** The card's sentence for that case, and the ring's spoken label. */
export const AWAITING_ALLOCATION_NOTE =
  'Your score appears after your first vault allocation.';

function bandForScore(score: number | null): HealthBand {
  if (score == null) return 'insufficient-evidence';
  if (score >= 90) return 'strong';
  if (score >= 75) return 'sound';
  if (score >= 60) return 'needs-attention';
  return 'at-risk';
}

export function deriveHealth(inputs: HealthInputs): HealthAssessment {
  const drivers = deriveDrivers(inputs);
  const { score, coverageBps } = overallFromDrivers(drivers);
  const band = bandForScore(score);

  return {
    score,
    band,
    bandLabel: HEALTH_BAND_LABELS[band],
    source: 'derived',
    provisional:
      score != null && coverageBps < HEALTH_REFERENCE.provisionalCoverageBps,
    coverageBps,
    drivers
  };
}

function overallWithDriverScore(
  drivers: HealthDriver[],
  id: HealthDriverId,
  newScore: number
): number | null {
  const substituted = drivers.map((entry) =>
    entry.id === id ? { ...entry, score: Math.round(newScore) } : entry
  );
  return overallFromDrivers(substituted).score;
}

/**
 * Deterministic modeled improvements: each one recomputes the affected
 * driver under a single modeled change, using exactly the formulas above.
 * Estimates only — nothing here moves funds or implies execution.
 */
export function deriveImprovements(
  inputs: HealthInputs,
  assessment: HealthAssessment
): HealthImprovement[] {
  const modeled: HealthImprovement[] = [];
  const repairs: HealthImprovement[] = [];
  const currentOverall = assessment.score;
  const driverById = new Map(
    assessment.drivers.map((entry) => [entry.id, entry])
  );

  const deltaFor = (id: HealthDriverId, newScore: number): number | null => {
    if (currentOverall == null) return null;
    const next = overallWithDriverScore(assessment.drivers, id, newScore);
    if (next == null) return null;
    return next - currentOverall;
  };

  // Invest idle capital above the reserve.
  const idle = idleShareOf(inputs);
  const idleDriver = driverById.get('idle-capital');
  if (
    idle &&
    idleDriver?.score != null &&
    idle.share > HEALTH_REFERENCE.idleReserveShare
  ) {
    const excess = idle.cash - HEALTH_REFERENCE.idleReserveShare * idle.covered;
    const modeledScore = 100;
    const scoreDelta = deltaFor('idle-capital', modeledScore);
    if (scoreDelta == null || scoreDelta >= 1) {
      modeled.push({
        id: 'invest-idle',
        title: 'Invest idle capital',
        detail: `${formatCompactUsd(excess, { maximumFractionDigits: 2 })} in cash sits above the ${formatPercent(HEALTH_REFERENCE.idleReserveShare, { from: 'fraction', digits: 0 })} reserve`,
        driverDelta: {
          label: DRIVER_LABELS['idle-capital'],
          from: idleDriver.score,
          to: modeledScore
        },
        scoreDelta,
        tradeoff:
          'Deployed capital follows vault exit timing instead of being instantly available',
        actionLabel: 'Browse vaults',
        href: '/vaults'
      });
    }
  }

  // Reduce the largest look-through exposure to the reference weight.
  const risk = inputs.risk;
  const diversificationDriver = driverById.get('diversification');
  if (
    risk &&
    diversificationDriver?.score != null &&
    risk.concentration.top1WeightBps != null &&
    risk.concentration.top1WeightBps >
      HEALTH_REFERENCE.largestAssetReferenceBps &&
    risk.exposures.byAsset.length > 1
  ) {
    const weights = risk.exposures.byAsset.map((asset) => asset.weightBps);
    const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);
    if (totalWeight > 0) {
      const shares = weights
        .map((weight) => weight / totalWeight)
        .sort((left, right) => right - left);
      const hhiScore = (values: number[]) =>
        100 * (1 - values.reduce((sum, share) => sum + share * share, 0));
      const current = hhiScore(shares);
      const targetTop = HEALTH_REFERENCE.largestAssetReferenceBps / 10_000;
      const [top, ...rest] = shares;
      const excess = top! - targetTop;
      const restTotal = 1 - top!;
      const trimmed = [
        targetTop,
        ...rest.map((share) =>
          restTotal > 0 ? share + (excess * share) / restTotal : share
        )
      ];
      const modeledDriverScore = Math.min(
        100,
        Math.max(
          0,
          diversificationDriver.score + Math.round(hhiScore(trimmed) - current)
        )
      );
      const scoreDelta = deltaFor('diversification', modeledDriverScore);
      const topAsset = risk.concentration.topAsset;
      const symbol =
        displaySymbol(
          topAsset?.tokenSymbol,
          topAsset?.symbol,
          topAsset?.address
        ) ?? 'largest asset';
      if (scoreDelta == null || scoreDelta >= 1) {
        modeled.push({
          id: 'reduce-largest-exposure',
          title: 'Reduce largest asset exposure',
          detail: `${symbol} is ${formatBasisPoints(risk.concentration.top1WeightBps, { fractionDigits: 1 })} of underlying exposure vs the ${formatBasisPoints(HEALTH_REFERENCE.largestAssetReferenceBps, { fractionDigits: 0 })} reference`,
          driverDelta: {
            label: DRIVER_LABELS['diversification'],
            from: diversificationDriver.score,
            to: modeledDriverScore
          },
          scoreDelta,
          tradeoff:
            'Modeled on current weights · execution path and fees excluded',
          actionLabel: 'Review exposure',
          href: '/risk'
        });
      }
    }
  }

  // Raise one-day liquidity to the reference floor.
  const t1 = oneDayBps(inputs);
  const liquidityDriver = driverById.get('liquidity');
  if (
    t1 != null &&
    liquidityDriver?.score != null &&
    t1 < HEALTH_REFERENCE.oneDayLiquidityReferenceBps
  ) {
    const modeledScore = Math.round(
      liquidityScore(HEALTH_REFERENCE.oneDayLiquidityReferenceBps)
    );
    const scoreDelta = deltaFor('liquidity', modeledScore);
    if (scoreDelta == null || scoreDelta >= 1) {
      const reference = HEALTH_REFERENCE.oneDayLiquidityReferenceBps;
      const withdrawableNow = isWithdrawableNowBasis(
        inputs.liquidity?.basis ?? ''
      );
      modeled.push({
        id: 'increase-liquidity',
        title: withdrawableNow
          ? 'Increase what you can withdraw now'
          : 'Increase liquidity available within one day',
        detail: withdrawableNow
          ? `${formatFlooredPercent(t1)} of your value can be withdrawn now, against the ${formatFlooredPercent(reference)} reference`
          : `${formatBasisPoints(t1, { fractionDigits: 0 })} of covered value exits within one day vs the ${formatBasisPoints(reference, { fractionDigits: 0 })} reference`,
        driverDelta: {
          label: DRIVER_LABELS['liquidity'],
          from: liquidityDriver.score,
          to: modeledScore
        },
        scoreDelta,
        tradeoff: 'May move money that earns yield into assets closer to cash',
        actionLabel: 'Review liquidity',
        href: '/liquidity'
      });
    }
  }

  // Evidence repairs — real gaps, no invented score impact.
  const coverage = inputs.holdings?.coverage;
  if (coverage) {
    const unpriced = coverage.holdingsTotal - coverage.pricedCount;
    if (unpriced > 0) {
      repairs.push({
        id: 'unpriced-holdings',
        title: 'Restore pricing coverage',
        detail: `${unpriced} holding${unpriced === 1 ? '' : 's'} without a current price are excluded from weights`,
        driverDelta: null,
        scoreDelta: null,
        tradeoff: 'Unpriced value is excluded, never assumed to be zero',
        actionLabel: 'Review holdings',
        href: '/portfolio'
      });
    }
    // Only while the wallet scan is out for now. Switched off on the
    // network, there is nothing the investor can do about it, so no card
    // offers an action: the idle tile already says tracking is off.
    if (walletTokensUntracked(coverage) === 'unavailable') {
      repairs.push({
        id: 'external-coverage',
        title: 'Include external wallet holdings',
        detail: WALLET_TOKENS_UNREADABLE,
        driverDelta: null,
        scoreDelta: null,
        tradeoff: 'ROWA positions remain fully covered meanwhile',
        actionLabel: 'Review holdings',
        href: '/portfolio'
      });
    }
  }

  modeled.sort(
    (left, right) => (right.scoreDelta ?? 0) - (left.scoreDelta ?? 0)
  );

  // First allocation unlocks the two vault-scoped checks — the highest-
  // leverage action for a funded wallet with no vault positions, so it
  // leads the list.
  const gateway: HealthImprovement[] = [];
  const totals = inputs.holdings?.totals;
  if (
    totals &&
    parseUsd(totals.coveredValueUsd) > 0 &&
    parseUsd(totals.rowaValueUsd) <= 0
  ) {
    gateway.push({
      id: 'first-allocation',
      title: 'Make your first vault allocation',
      detail:
        'Diversification and liquidity checks activate with your first ROWA vault position',
      driverDelta: null,
      scoreDelta: null,
      impactLabel: 'Unlocks 2',
      resultLabel: 'Diversification & liquidity checks',
      tradeoff: 'Allocated capital follows vault exit timing',
      actionLabel: 'Browse vaults',
      href: '/vaults'
    });
  }

  return [...gateway, ...modeled, ...repairs];
}
